import React, { useContext, useMemo, useRef } from 'react';
import classNames from 'classnames';
import { RightClickDropdown } from 'components/RightClickDropdown';
import { FileTypeRFI } from 'router/subrouters/DataRoom/models/DataRoomRfi';
import { useTranslation } from 'react-i18next';
import TruckIcon from 'icons/Truck.icon';
import MinusCircleIcon from 'icons/MinusCircle.icon';
import MymeTypeIcon from 'router/subrouters/DataRoom/components/DataRoomDocumentsRouter/components/MymeTypeIcon';
import ThreeDotsDropdown from 'components/ThreeDotsDropdown';
import { DropdownRef } from 'ncoded-component-library/build/components/molecules/Dropdown/Dropdown.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import { ALIGNMENT } from 'models/Global';
import FilePreviewModal from 'components/FilePreviewModal';
import {
  getIsRfiFileStatusAdded,
  getIsRfiFileStatusUploaded,
} from 'router/subrouters/DataRoom/utils';
import { getUserFullName } from 'utils';
import useMatchMedia from 'hooks/useMatchMedia';
import UserIcon from 'icons/User.icon';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import { NewTag } from 'router/subrouters/DataRoom/components/NewTag';
import RfiContext from 'router/subrouters/DataRoom/providers/Rfi/Rfi.context';

import './DocumentInstanceInList.styles.scss';
import './DocumentInstanceInList.styles.responsive.scss';

type DocumentInstanceInListProps = {
  className?: string;
  rfiId: string;
  document: FileTypeRFI;
  onIgnoreDocument?: () => void;
  onRemoveDocument?: () => void;
  onAddToDataRoom?: () => void;
  onMoveWithinDataRoom?: () => void;
};

const DocumentInstanceInList: React.FC<DocumentInstanceInListProps> = (
  props,
) => {
  const {
    className,
    rfiId,
    document,
    onAddToDataRoom,
    onIgnoreDocument,
    onRemoveDocument,
    onMoveWithinDataRoom,
  } = props;

  const dropdownRef = useRef<DropdownRef>(null);
  const [modal, modalRef] = useCallbackRef<OverlayRef>();

  const { doesntHaveAccess } = useContext(DataRoomContext);

  const {
    status,
    file,
    dataRoomNodeId,
    description,
    dataRoomRFIFileUploader,
    createdAt,
  } = document;

  const { getIsNew } = useContext(RfiContext);

  const { t } = useTranslation();

  const isPhablet = useMatchMedia('isPhablet');

  const dropdownOptions = useMemo(
    () => [
      ...(getIsRfiFileStatusUploaded(status)
        ? [
            {
              label: t('addToDataRoom'),
              icon: <TruckIcon />,
              onClick: onAddToDataRoom,
            },
            {
              label: t('ignore'),
              icon: <MinusCircleIcon />,
              onClick: onIgnoreDocument,
              alert: true,
            },
          ]
        : []),
      ...(getIsRfiFileStatusAdded(status) && dataRoomNodeId
        ? [
            {
              label: t('moveWithinDR'),
              icon: <TruckIcon />,
              onClick: onMoveWithinDataRoom,
            },
            {
              label: t('removeFromDR'),
              icon: <MinusCircleIcon />,
              onClick: onRemoveDocument,
              alert: true,
            },
          ]
        : []),
    ],
    [
      status,
      dataRoomNodeId,
      onAddToDataRoom,
      onIgnoreDocument,
      onMoveWithinDataRoom,
      onRemoveDocument,
      t,
    ],
  );

  const hasOptions = !doesntHaveAccess && !!dropdownOptions.length;
  const rightClickDisabled = !hasOptions;

  const renderThreeDotsDropdown = useMemo(
    () =>
      hasOptions && (
        <ThreeDotsDropdown
          ref={dropdownRef}
          options={dropdownOptions}
          className="document-instance-in-list__threedot"
          contentClassName="document-instance-in-list__threedot-content"
          withBorder={false}
          alignment={ALIGNMENT.VERTICAL}
          renderAsPortal
        />
      ),
    [dropdownOptions, hasOptions],
  );

  const classes = classNames('document-instance-in-list', className);

  return (
    <RightClickDropdown options={dropdownOptions} disabled={rightClickDisabled}>
      <div className={classes}>
        <div>
          <div className="file-name">
            {file?.type && <MymeTypeIcon mimeType={file?.type} />}
            <p
              onClick={modal?.open}
              className={classNames({ clickable: file })}
            >
              {file?.name}
              <NewTag variant="green" isNew={getIsNew(rfiId, createdAt)} />
            </p>

            {isPhablet && renderThreeDotsDropdown}
          </div>

          {dataRoomRFIFileUploader?.firstName && (
            <div className="user-name">
              {isPhablet && <UserIcon />}
              <p>
                {getUserFullName(
                  dataRoomRFIFileUploader.firstName,
                  dataRoomRFIFileUploader.lastName,
                )}
              </p>
            </div>
          )}
          {description && <p>{description}</p>}
        </div>

        <div>
          {!getIsRfiFileStatusUploaded(status) && (
            <div className={classNames('status', status)}>{status}</div>
          )}

          {!isPhablet && renderThreeDotsDropdown}
        </div>
      </div>

      {file && (
        <FilePreviewModal
          ref={modalRef}
          url={file.url}
          type={file.originalType || file.type}
        />
      )}
    </RightClickDropdown>
  );
};

export default DocumentInstanceInList;
